import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses.js';
import { OfferSubCategory } from '../../common/enums.js';
import { t } from '../../common/i18n/index.js';
import { isMandatoryAddon } from '../../common/utils/productOfferingsUtils.js';
import { PlanProductRuntimeCharacteristic } from '../PlaceOrder/utils/characteristicsFlagsUtilsCoCo.js';
import { BundleAddonGroupRadioCardCoCo } from '../ProductOfferingAddonCoCo/BundleAddonGroupRadioCardCoCo.js';
import { InstallationServiceSelectorCoCo } from '../ProductOfferingAddonCoCo/InstallationServiceSelectorCoCo.js';
import { ProductOfferAddOn } from '../ProductOfferingAddonCoCo/ProductOfferAddOnCoCo.js';
import { ProductOfferingCharacteristicSelectorCoCo } from '../ProductOfferingAddonCoCo/ProductOfferingCharacteristicSelectorCoCo.js';
import {
  calculateFixedBroadbandPrices,
  initializeFixedBroadbandGroupSelection,
  isInstallationServiceGroup,
} from './FixedBroadbandConfigCoCoUtils.js';
import { getRecurringOfferingPriceInEuros } from './ProductOfferingsModalCoCoUtils.js';
import type { ProductOfferingsModalCoCoConfig } from './ProductOfferingsModalCoCo.js';

import './FixedBroadbandModalCoCo.scss';

export const fixedBroadbandConfig: ProductOfferingsModalCoCoConfig = {
  getAddOnPriceInEuros: getRecurringOfferingPriceInEuros,
  initializeGroupSelection: initializeFixedBroadbandGroupSelection,
  calculatePrices: calculateFixedBroadbandPrices,
  renderContent: ({
    productOfferings,
    descriptionItems,
    bundledOfferings,
    bundleAddonGroups,
    groupSelections,
    groupValidationErrors,
    selectedRuntimeCharacteristicValue,
    onRuntimeCharacteristicValueChange,
    onAddOnChange,
    onGroupSelectionChange,
    convertToProductOfferingAddOn,
  }) => {
    const isFiber = productOfferings.productOfferingType?.subcategory === OfferSubCategory.FIXED_BROADBAND;
    const includedServices = bundledOfferings.filter(offering => isMandatoryAddon(offering));
    const additionalServices = bundledOfferings.filter(offering => !isMandatoryAddon(offering));
    const equipmentGroups = bundleAddonGroups.filter(group => !isInstallationServiceGroup(group));
    const installationGroups = bundleAddonGroups.filter(isInstallationServiceGroup);

    const renderAddOn = (offering: (typeof bundledOfferings)[number]) => (
      <div className={`${dsClass.MARGIN_TOP_2}`} key={offering.id}>
        <ProductOfferAddOn {...convertToProductOfferingAddOn(offering, onAddOnChange)} />
      </div>
    );

    return (
      <>
        <div className="of-subscription-modal-details-description">
          <CL.Description
            listTitle={t.NUCX('Subscription contents')}
            items={descriptionItems.map(item => ({ title: item.title, description: item.content }))}
          />
        </div>
        {isFiber && (
          <ProductOfferingCharacteristicSelectorCoCo
            characteristics={[...(productOfferings.planProductOffering?.productOfferingRuntimeCharacteristic ?? [])]}
            characteristicName={PlanProductRuntimeCharacteristic.FIBER_MODE}
            heading={t.RZQ4('Fiber type')}
            description={t.X04A(
              'Select the fiber type for your connection. Contact your property manager if you are unsure about the fiber type.'
            )}
            selectedValue={selectedRuntimeCharacteristicValue}
            onValueChange={onRuntimeCharacteristicValueChange}
          />
        )}
        {includedServices.length > 0 && (
          <div className="of-modal-section">
            <div className="of-modal-section__heading">{t.GK6I('Services included')}</div>
            {includedServices.map(renderAddOn)}
          </div>
        )}
        {equipmentGroups.length > 0 && (
          <div className="of-modal-section">
            <div className="of-modal-section__heading">{t.JVHX('Devices included')}</div>
            {equipmentGroups.map(group => {
              const selection = groupSelections.find(item => item.groupId === group.id);
              return (
                <BundleAddonGroupRadioCardCoCo
                  key={group.id}
                  group={group}
                  selectedOfferingIds={selection?.selectedOfferingIds ?? []}
                  onSelectionChange={onGroupSelectionChange}
                  errorMessage={groupValidationErrors[group.id]}
                />
              );
            })}
          </div>
        )}
        {installationGroups.length > 0 && (
          <div className="of-modal-section">
            {installationGroups.map(group => {
              const selection = groupSelections.find(item => item.groupId === group.id);
              return (
                <InstallationServiceSelectorCoCo
                  key={group.id}
                  group={group}
                  selectedOfferingIds={selection?.selectedOfferingIds ?? []}
                  onSelectionChange={onGroupSelectionChange}
                  errorMessage={groupValidationErrors[group.id]}
                />
              );
            })}
          </div>
        )}
        {additionalServices.length > 0 && (
          <div className="of-modal-section">
            <div className="of-modal-section__heading">{t.UU5G('Select additional services')}</div>
            {additionalServices.map(renderAddOn)}
          </div>
        )}
      </>
    );
  },
};
